import type { MetaFunction } from 'react-router'
import { STATIC_PAGE_HEADERS } from "~/lib/cache.server"
import { Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { Shield, BadgeCheck, FileCheck, Star, Users, AlertTriangle, ArrowRight } from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Charte qualité — Comment nous vérifions les artisans | AfficheServices' },
  { name: 'description', content: 'Découvrez comment AfficheServices vérifie chaque artisan : SIRET, assurance RC Pro, identité, avis clients. Notre engagement qualité.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/charte-qualite' },
  { property: 'og:image', content: 'https://www.afficheservices.fr/api/og?title=Charte+qualite&subtitle=Nos+engagements' },
]

export default function CharteQualite() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-16 lg:px-6">
        <div className="text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <h1 className="mt-4 text-3xl font-bold text-slate-900">Notre charte qualité</h1>
          <p className="mt-3 text-sm text-slate-500 leading-relaxed">
            Chaque professionnel inscrit sur AfficheServices passe par un processus de vérification rigoureux.
            Votre sécurité et votre satisfaction sont notre priorité.
          </p>
        </div>

        {/* Verification steps */}
        <div className="mt-12 space-y-6">
          {[
            {
              icon: BadgeCheck, color: 'bg-blue-50 text-blue-600',
              title: '1. Vérification du SIRET',
              desc: 'Chaque numéro SIRET est vérifié automatiquement via le registre SIRENE de l\'INSEE. Nous confirmons que l\'entreprise est active et correctement enregistrée.',
            },
            {
              icon: Shield, color: 'bg-emerald-50 text-emerald-600',
              title: '2. Assurance RC Professionnelle',
              desc: 'Nous vérifions que chaque artisan dispose d\'une assurance Responsabilité Civile Professionnelle en cours de validité. Cette assurance vous protège en cas de dommages pendant les travaux.',
            },
            {
              icon: FileCheck, color: 'bg-violet-50 text-violet-600',
              title: '3. Vérification d\'identité',
              desc: 'L\'identité du gérant est vérifiée lors de l\'inscription. Nous nous assurons que la personne qui s\'inscrit est bien le responsable légal de l\'entreprise.',
            },
            {
              icon: Star, color: 'bg-amber-50 text-amber-600',
              title: '4. Suivi des avis clients',
              desc: 'Chaque avis est modéré par notre équipe. Les faux avis sont supprimés. Les professionnels avec une note inférieure à 3/5 font l\'objet d\'un suivi renforcé.',
            },
            {
              icon: Users, color: 'bg-cyan-50 text-cyan-600',
              title: '5. Maximum 3 devis par demande',
              desc: 'Pour garantir la qualité du service, nous envoyons chaque demande à 3 professionnels maximum. Pas de concurrence massive, pas de spam.',
            },
            {
              icon: AlertTriangle, color: 'bg-red-50 text-red-600',
              title: '6. Politique de tolérance zéro',
              desc: 'En cas de litige signalé, nous suspendons immédiatement le profil du professionnel le temps de l\'enquête. Les professionnels non conformes sont définitivement exclus.',
            },
          ].map((step) => (
            <div key={step.title} className="flex gap-4 rounded-xl border border-slate-100 p-5">
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${step.color}`}>
                <step.icon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">{step.title}</h3>
                <p className="mt-1 text-sm text-slate-500 leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div className="mt-12 grid grid-cols-3 gap-4">
          {[
            { value: '100%', label: 'SIRET vérifiés' },
            { value: '3 max', label: 'Devis par demande' },
            { value: '48h', label: 'Délai de réponse' },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-slate-100 bg-slate-50 p-5 text-center">
              <p className="text-2xl font-bold text-primary">{s.value}</p>
              <p className="mt-1 text-xs text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 rounded-2xl bg-primary/5 p-8 text-center">
          <h2 className="text-xl font-semibold text-slate-900">Besoin d'un artisan de confiance ?</h2>
          <p className="mt-2 text-sm text-slate-500">Recevez 3 devis de professionnels vérifiés, gratuitement et sans engagement.</p>
          <Link to="/demande-devis" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary-hover btn-press">
            Demander un devis <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
      {/* Normes et certifications */}
      <section className="border-t border-slate-100 bg-surface-secondary py-14">
        <div className="mx-auto max-w-3xl px-4 lg:px-6">
          <h2 className="text-xl font-bold text-slate-900">Normes et certifications vérifiées</h2>
          <div className="mt-6 space-y-3 text-sm leading-relaxed text-slate-600">
            <p><strong>SIRET et inscription au registre des métiers :</strong> Chaque professionnel est vérifié via le registre SIRENE de l'INSEE. Un numéro SIRET actif est la preuve que l'entreprise existe légalement et est à jour de ses obligations fiscales et sociales.</p>
            <p><strong>Assurance RC Professionnelle :</strong> L'assurance Responsabilité Civile Professionnelle couvre les dommages causés aux tiers pendant l'exécution des travaux. Elle est obligatoire pour tous les artisans du bâtiment (article L.241-1 du Code des assurances).</p>
            <p><strong>Garantie décennale :</strong> Pour les travaux de construction et de rénovation lourde, la garantie décennale couvre les défauts de construction pendant 10 ans. Nous vérifions que l'attestation est en cours de validité et couvre la période de vos travaux.</p>
            <p><strong>Qualification RGE :</strong> Pour les travaux de rénovation énergétique, nous indiquons si l'artisan est certifié RGE (Reconnu Garant de l'Environnement) — condition nécessaire pour que le client bénéficie de MaPrimeRénov', des CEE et de l'éco-PTZ.</p>
          </div>
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Références réglementaires</p>
            <ul className="mt-2 space-y-1 text-xs text-slate-500">
              <li>• Code des assurances, article L.241-1 — Obligation d'assurance décennale</li>
              <li>• <a href="https://www.qualibat.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Qualibat</a> — Certification des entreprises du bâtiment</li>
              <li>• <a href="https://www.qualit-enr.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Qualit'ENR</a> — Qualification RGE énergies renouvelables</li>
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
